import React from 'react';
import { Newspaper, ExternalLink } from 'lucide-react';

export default function NewsCard({ data, loading }) {
  if (loading) {
    return (
      <div className="puvio-card p-5" data-testid="news-card-skeleton">
        <div className="puvio-skeleton h-6 w-32 mb-3" />
        <div className="puvio-skeleton h-4 w-full mb-2" />
        <div className="puvio-skeleton h-4 w-5/6 mb-2" />
        <div className="puvio-skeleton h-4 w-4/6" />
      </div>
    );
  }
  const items = data?.articles || [];
  const lead = items[0];
  const rest = items.slice(1, 5);
  return (
    <div className="puvio-card p-5" data-testid="news-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">Local pulse</p>
          <p className="text-sm text-[#4A5A50] mt-0.5">News from {data?.city || 'your city'}</p>
        </div>
        <div className="w-10 h-10 rounded-2xl bg-[#C4FF3D]/30 flex items-center justify-center">
          <Newspaper className="w-5 h-5 text-[#15291C]" />
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-[#4A5A50]">No fresh local news right now.</p>
      )}

      {lead && (
        <a
          href={lead.url}
          target="_blank"
          rel="noreferrer"
          className="block rounded-2xl overflow-hidden border border-black/5 bg-[#0F1A13]/05 mb-3 group puvio-focus"
          data-testid="news-lead"
        >
          {lead.image && (
            <div className="aspect-[16/9] overflow-hidden bg-black/5">
              <img src={lead.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
            </div>
          )}
          <div className="p-3">
            <p className="text-sm font-semibold leading-snug line-clamp-2 group-hover:underline">{lead.title}</p>
            <p className="text-[11px] text-[#4A5A50] mt-1 flex items-center gap-1.5">
              <span className="truncate">{lead.source || lead.domain}</span>
              {lead.published_at && (<><span>·</span><span>{lead.published_at}</span></>)}
              <ExternalLink className="w-3 h-3 ml-auto shrink-0" />
            </p>
          </div>
        </a>
      )}

      <ul className="flex flex-col gap-2.5">
        {rest.map((a, i) => (
          <li key={a.url || `${a.title}-${i}`} className="group" data-testid={`news-item-${i}`}>
            <a href={a.url} target="_blank" rel="noreferrer" className="flex gap-3 p-2 -mx-2 rounded-xl hover:bg-[#0F1A13]/05 transition-colors puvio-focus">
              <span className="w-1 self-stretch rounded-full bg-[#C4FF3D]" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-[#0F1A13] line-clamp-2 leading-snug group-hover:underline">{a.title}</span>
                <span className="flex items-center gap-1.5 text-[11px] text-[#4A5A50] mt-1">
                  <span className="truncate">{a.source || a.domain}</span>
                  {a.published_at && (<><span>·</span><span>{a.published_at}</span></>)}
                  <ExternalLink className="w-3 h-3 ml-auto shrink-0" />
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
      {data?.source_label && (
        <p className="text-[10px] text-[#7A8A80] mt-3">Local headlines</p>
      )}
    </div>
  );
}
